import { useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { opsApi } from '@/api/ops';
import type { JobStatus } from '@/api/opsTypes';
import { PERMISSIONS } from '@/api/types';
import { Select } from '@/components/Select';
import { StatusChip } from '@/components/ops/Badges';
import { LoadMore, PageHeader, Section } from '@/components/ops/Layout';
import { RequirePermission } from '@/components/ops/PermissionGate';
import { QueryState } from '@/components/ops/Problem';
import { formatTimestamp } from '@/ops/format';
import { REPLICA } from '@/ops/live';
import { jobStatusText } from '@/ops/stepAdvice';
import { stepName } from '@/ops/stepNames';
import { useEnvironments } from '@/ops/useEnvironment';
import { formatWhen } from '@/pages/AccountsPage';

const STATUSES: JobStatus[] = ['pending', 'running', 'failed', 'done', 'done_with_warnings', 'cancelled'];

/** Setup jobs, newest first (keyset paged). Each is a durable, resumable job (CP1) — one per tenant created here. */
export function ProvisioningJobsPage() {
  const environments = useEnvironments();
  const [env, setEnv] = useState('');
  const [status, setStatus] = useState('');

  const jobs = useInfiniteQuery({
    queryKey: ['ops', 'jobs', env, status],
    queryFn: ({ pageParam }) => opsApi.provisioning.listJobs({ environment: env || undefined, status: status || undefined, limit: 50, cursor: pageParam }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    ...REPLICA,
    // A running job changes under the list; a light refresh keeps the statuses honest.
    refetchInterval: (query) =>
      query.state.data?.pages.some((p) => p.jobs.some((j) => j.status === 'running' || j.status === 'pending')) ? 10_000 : false,
  });
  const rows = jobs.data?.pages.flatMap((p) => p.jobs) ?? [];

  return (
    <RequirePermission permission={PERMISSIONS.tenantProvision} what="Setup jobs">
      <PageHeader
        title="Setup jobs"
        subtitle="One per tenant created from the console. A stopped setup can be retried; nothing is set up twice."
        actions={
          <>
            <Link to="/ops/provisioning/catalog" className="btn btn-ghost">Setup steps</Link>
            <Link to="/ops/provisioning/new" className="btn btn-primary">Create a tenant</Link>
          </>
        }
      />

      <div className="filterbar ops-filters">
        <Select label="Environment" inline value={env} onChange={setEnv} placeholder="All environments"
          options={(environments.data?.environments ?? []).map((e) => ({ value: e.key, label: e.label }))} />
        <Select label="Status" inline value={status} onChange={setStatus} placeholder="Any status" searchable={false}
          options={STATUSES.map((s) => ({ value: s, label: jobStatusText(s) }))} />
      </div>

      <Section title="Setups" meta={`${rows.length}${jobs.hasNextPage ? '+' : ''} shown`} flush>
        <QueryState query={{ ...jobs, data: jobs.data ? rows : undefined }}>
          {(list) => list.length === 0 ? (
            <div className="empty-state t-body">No setups for this filter.</div>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr><th>Tenant</th><th>Environment</th><th>Status</th><th>Step</th><th>Started by</th><th>Updated</th></tr>
                </thead>
                <tbody>
                  {list.map((job) => (
                    <tr key={job.id}>
                      <td>
                        <Link to={`/ops/provisioning/jobs/${job.id}`} style={{ fontWeight: 600 }}>{job.label}</Link>
                        {job.op === 'delete' && <> <StatusChip status="deletion" tone="danger" /></>}
                        {job.op === 'discard' && <> <StatusChip status="clean-up" tone="warning" /></>}
                        <div className="t-caption muted">
                          {job.kind === 'external' ? 'external account' : job.industry_pack === 'none' ? 'no industry' : job.industry_pack} · {job.plan_code} plan
                        </div>
                      </td>
                      <td className="mono">{job.environment}</td>
                      <td>
                        <StatusChip status={job.status} title={jobStatusText(job.status)} />
                        {job.password_available && <div className="t-caption note-error">password waiting to be revealed</div>}
                      </td>
                      <td className="t-caption" title={job.current_step ?? undefined}>{job.current_step ? stepName(job.current_step) : '—'}</td>
                      <td className="t-caption">{job.created_by.name}<div className="muted">{formatTimestamp(job.created_at)}</div></td>
                      <td className="t-caption" title={formatTimestamp(job.updated_at)}>{formatWhen(job.updated_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </QueryState>
        <LoadMore hasMore={!!jobs.hasNextPage} loading={jobs.isFetchingNextPage} onLoad={() => jobs.fetchNextPage()} />
      </Section>
    </RequirePermission>
  );
}
